import { useCallback } from 'react';
import useSWR from 'swr';
import { getQueriesForClient } from '~/app/_repositories/query-service';
import { convertIStorySeriesAvailabilityToUserAvailability } from '~/common/converter/user-availability/user-availability';
import { useTellerSession } from '~/common/hooks/use-auth/use-teller-session';
import { SeriesAvailability } from '~/common/types/series-availability';
import { UserAvailability } from '~/common/types/user-availability';

interface Data {
  userAvailability: UserAvailability | undefined;
  isCheckingUserAvailability: boolean | undefined;
}

export const useFetchSeriesUserAvailability = (
  seriesId: string,
  seriesAvailability: SeriesAvailability
): Data => {
  const { session } = useTellerSession();

  const fetchUserAvailability = useCallback(async () => {
    if (seriesAvailability === SeriesAvailability.PUBLIC) {
      return UserAvailability.REQUIRE_NONE;
    }

    if (!session?.uid) return;

    const result = await getQueriesForClient({
      request_list: [
        {
          request_id: `series-${seriesId}-user-availability`,
          series_request: {
            id: seriesId,
            require_series: {
              require_user_availability: true,
            },
          },
        },
      ],
    });
    const userAvailability =
      result?.response_list[0]?.series?.user_availability;

    if (!result && !userAvailability) {
      return;
    }

    return convertIStorySeriesAvailabilityToUserAvailability(userAvailability);
  }, [session?.uid, seriesAvailability, seriesId]);

  const { data: userAvailability, isValidating: isCheckingUserAvailability } =
    useSWR(
      ['/series/user-availability', seriesId, session?.uid],
      fetchUserAvailability
    );

  return {
    userAvailability,
    isCheckingUserAvailability,
  };
};
